import { useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import { faCalendar, faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Form from 'components/form';
import { TaskFormValues } from 'components/tasks/utils/useTaskForm';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isCurrentYear } from 'helpers/dates';
import { DateTime } from 'luxon';
import { twMerge } from 'tailwind-merge';

interface DueDateFormFieldProps extends Popover.PopoverProps {}
const DueDateFormField = ({ children, ...rest }: DueDateFormFieldProps) => {
    const { timezone } = useCurrentAccount();
    const { path, setValue, watch, control } = Form.useGivebutterFormContext<TaskFormValues>();
    const due_at = watch('due_at');

    const [isOpen, setIsOpen] = useState(false);

    const isPastDue =
        due_at && due_at.setZone(timezone).endOf('day') < DateTime.now().setZone(timezone);

    return (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen} {...rest}>
            <Popover.Trigger asChild>
                {due_at ? (
                    <div className="cursor-pointer rounded-lg border border-gray-200 py-2 pl-3 pr-1.5 hover:bg-gray-100">
                        <div className="flex items-center gap-1.5">
                            <FontAwesomeIcon
                                icon={faCalendar}
                                size="sm"
                                className={twMerge('text-gray-500', isPastDue && 'text-red-500')}
                            />

                            <span className="text-sm">
                                Due{' '}
                                {due_at.toFormat(`LLL d${isCurrentYear(due_at) ? '' : ', yyyy'}`)}
                            </span>
                            <Button
                                variant="none"
                                color="neutral"
                                onClick={e => {
                                    e.preventDefault();
                                    setValue('due_at', null, { shouldDirty: true });
                                }}
                                className="px-1"
                            >
                                <FontAwesomeIcon icon={faClose} />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Button
                        variant="transparent"
                        shape="square"
                        className="hover:bg-gray-100"
                        aria-label="Choose date"
                    >
                        <FontAwesomeIcon icon={faCalendar} className="text-gray-500" />
                    </Button>
                )}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content align="start" side="bottom">
                    <Form.UseFormLabel name={path('due_at')} className="sr-only" />
                    <Form.UseFormDatePicker
                        control={control}
                        name="due_at"
                        isTextInput={false}
                        selectRange={false}
                        onChange={() => {
                            setIsOpen(false);
                        }}
                    />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

export { DueDateFormField };
